import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/auth/AuthContext";
import { toast } from "sonner";
import {
    Loader2,
    RefreshCw,
    Zap,
    ShieldCheck,
    AlertTriangle,
    Search,
    DollarSign,
    Send,
} from "lucide-react";

const authHeaders = () => {
    const t = localStorage.getItem("session_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
        });
    } catch {
        return iso;
    }
}

const STATUS_BY_PAY = {
    paid: "bg-mint text-forest",
    unpaid: "bg-gold/30 text-burnt",
    no_payment_required: "bg-mint text-forest",
};

export default function AdminStripeEvents() {
    const [data, setData] = useState(null);
    const [health, setHealth] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [eventsRes, healthRes] = await Promise.all([
                axios.get(`${API}/admin/stripe-events?limit=100`, {
                    headers: authHeaders(),
                }),
                axios.get(`${API}/admin/stripe/webhook-health`, {
                    headers: authHeaders(),
                }),
            ]);
            setData(eventsRes.data);
            setHealth(healthRes.data);
        } catch {
            toast.error("Couldn't load Stripe events");
            setData({ events: [], stats: {} });
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (!data) {
        return (
            <div className="flex items-center gap-2 text-muted2">
                <Loader2 className="animate-spin" size={16} /> Loading Stripe events…
            </div>
        );
    }

    return (
        <div data-testid="admin-stripe-events-panel">
            <WebhookHealthPanel health={health} loading={busy} />
            <ReconcileTool onReconciled={load} />

            <div className="flex items-center justify-between mb-5 mt-8">
                <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                    Last {data.events.length} webhook{data.events.length === 1 ? "" : "s"}
                </p>
                <button
                    data-testid="admin-stripe-refresh"
                    onClick={load}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-forest hover:text-forestDark disabled:opacity-50"
                >
                    <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
                <Stat label="All-time webhooks" value={data.stats.total ?? 0} />
                <Stat label="Last 24 hours" value={data.stats.last_24h ?? 0} />
            </div>

            {data.events.length === 0 ? (
                <div className="bg-white rounded-[20px] p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                    <Zap size={36} className="mx-auto text-mint" />
                    <p className="mt-3 font-bold text-navy">No Stripe webhooks yet</p>
                    <p className="mt-1 text-sm text-muted2">
                        Once Stripe delivers an event to your endpoint it&apos;ll appear here.
                    </p>
                </div>
            ) : (
                <ul className="space-y-2">
                    {data.events.map((e) => {
                        const pillColor = STATUS_BY_PAY[e.payment_status] || "bg-muted2/20 text-muted2";
                        return (
                            <li
                                key={e.event_id}
                                data-testid={`stripe-event-${e.event_id}`}
                                className="bg-white rounded-xl px-4 py-3 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex items-start justify-between gap-3 flex-wrap"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-xs text-navy break-all">
                                            {e.event_type || "—"}
                                        </span>
                                        {e.payment_status && (
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${pillColor}`}>
                                                {e.payment_status}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-0.5 text-[11px] text-muted2 font-mono break-all">
                                        {e.event_id}
                                    </p>
                                    {e.session_id && (
                                        <p className="mt-0.5 text-[11px] text-muted2 font-mono break-all">
                                            session: {e.session_id}
                                        </p>
                                    )}
                                </div>
                                <span className="text-[11px] text-muted2 whitespace-nowrap">
                                    {fmtDate(e.received_at)}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white rounded-[16px] p-4 shadow-[0_4px_16px_rgba(0,0,0,0.05)]">
            <p className="text-[10px] uppercase tracking-widest font-bold text-muted2">{label}</p>
            <p className="mt-1 font-serif font-black text-2xl text-forest">{value}</p>
        </div>
    );
}

function WebhookHealthPanel({ health, loading }) {
    if (loading && !health) return null;
    if (!health) return null;

    const eventsTotal = health.events?.total ?? 0;
    const last24h = health.events?.last_24h ?? 0;
    const apiKeyMode = health.stripe_api_key_mode;
    const secretConfigured = health.webhook_secret_configured;
    const endpoints = health.stripe_endpoints || [];

    // Determine the diagnostic state
    let state = "healthy";
    let stateLabel = "Healthy";
    let stateColor = "bg-forest text-white";
    let advice = "";

    if (!secretConfigured) {
        state = "broken";
        stateLabel = "Webhook secret missing";
        stateColor = "bg-burnt text-white";
        advice =
            "STRIPE_WEBHOOK_SECRET is not set on this environment. Add it from your Stripe Dashboard → Developers → Webhooks → the endpoint's Signing Secret.";
    } else if (endpoints.length === 0 && !health.stripe_api_error) {
        state = "broken";
        stateLabel = "No webhook configured in Stripe";
        stateColor = "bg-burnt text-white";
        advice =
            "Stripe has zero webhook endpoints configured for this API key. Add one in Stripe Dashboard → Developers → Webhooks pointing at /api/webhook/stripe.";
    } else if (eventsTotal === 0 && endpoints.length > 0) {
        state = "warning";
        stateLabel = "Endpoint registered but 0 events received";
        stateColor = "bg-gold text-navy";
        advice =
            "Stripe shows your endpoint is enabled but we&apos;ve never received a successful event. Most common cause: the STRIPE_WEBHOOK_SECRET on this environment doesn&apos;t match the secret on the Stripe endpoint (signature verification fails silently). Copy the Signing Secret from the endpoint in Stripe Dashboard and update your environment variable.";
    } else if (last24h === 0 && endpoints.length > 0) {
        state = "info";
        stateLabel = "No events in last 24h";
        stateColor = "bg-mint text-forest";
        advice =
            "Webhook is wired up but quiet. That&apos;s normal if you haven&apos;t had any checkout activity today.";
    }

    return (
        <div
            data-testid="webhook-health-panel"
            className="bg-white rounded-[20px] p-5 shadow-[0_6px_24px_rgba(0,0,0,0.07)] mb-5"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-2">
                    {state === "healthy" || state === "info" ? (
                        <ShieldCheck size={20} className="text-forest" />
                    ) : (
                        <AlertTriangle size={20} className="text-burnt" />
                    )}
                    <h3 className="font-serif font-black text-navy text-lg">
                        Webhook health
                    </h3>
                </div>
                <span
                    data-testid="webhook-health-state"
                    className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${stateColor}`}
                >
                    {stateLabel}
                </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
                <Pill
                    label="API key"
                    value={apiKeyMode || "?"}
                    ok={apiKeyMode === "live"}
                />
                <Pill
                    label="Webhook secret"
                    value={secretConfigured ? "Set" : "Missing"}
                    ok={secretConfigured}
                />
                <Pill
                    label="Endpoints in Stripe"
                    value={endpoints.length}
                    ok={endpoints.length > 0}
                />
                <Pill
                    label="Events / 24h"
                    value={last24h}
                    ok={last24h > 0 || eventsTotal > 0}
                />
            </div>

            {advice && (
                <p className="text-sm text-navy bg-cream/60 border border-mint rounded-xl p-3 mb-2">
                    <strong>Diagnosis: </strong>
                    {advice}
                </p>
            )}

            {endpoints.length > 0 && (
                <ul className="space-y-1.5 mt-2">
                    {endpoints.map((ep) => (
                        <li
                            key={ep.id}
                            className="text-[11px] font-mono break-all bg-cream/40 rounded-lg px-3 py-1.5 text-navy"
                        >
                            <span
                                className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase mr-2 ${
                                    ep.status === "enabled"
                                        ? "bg-forest text-white"
                                        : "bg-burnt text-white"
                                }`}
                            >
                                {ep.status}
                            </span>
                            {ep.livemode ? (
                                <span className="text-[9px] font-bold text-forest mr-2">LIVE</span>
                            ) : (
                                <span className="text-[9px] font-bold text-burnt mr-2">TEST</span>
                            )}
                            {ep.url}
                        </li>
                    ))}
                </ul>
            )}

            {health.stripe_api_error && (
                <p className="text-xs text-burnt mt-2">
                    Couldn&apos;t reach Stripe API: {health.stripe_api_error}
                </p>
            )}
        </div>
    );
}

function Pill({ label, value, ok }) {
    return (
        <div
            className={`rounded-xl p-2 text-center border ${
                ok ? "border-mint bg-cream/40" : "border-burnt/50 bg-burnt/10"
            }`}
        >
            <p className="text-[9px] uppercase tracking-widest font-bold text-muted2">
                {label}
            </p>
            <p
                className={`mt-0.5 font-bold text-sm ${
                    ok ? "text-forest" : "text-burnt"
                }`}
            >
                {String(value)}
            </p>
        </div>
    );
}

function ReconcileTool({ onReconciled }) {
    const [stripeId, setStripeId] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const reconcile = async () => {
        const id = stripeId.trim();
        if (!id) return;
        setBusy(true);
        setResult(null);
        try {
            const res = await axios.post(
                `${API}/admin/stripe/reconcile`,
                { stripe_id: id },
                { headers: authHeaders() },
            );
            setResult(res.data);
            if (res.data?.action_taken === "marked_paid_and_emailed") {
                toast.success(
                    "Order rescued · marked paid + confirmation email sent",
                );
                onReconciled?.();
            } else if (res.data?.action_taken === "marked_paid") {
                toast.success("Order marked paid (email send queued)");
                onReconciled?.();
            } else if (res.data?.action_taken === "already_marked_paid") {
                toast.info("Order is already marked paid — no change");
            } else if (res.data?.action_taken === "no_op" && !res.data?.order) {
                toast.warning("Stripe found, but no matching order in our DB");
            } else {
                toast.info(`Stripe says: ${res.data?.action_taken || "unknown"}`);
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Reconciliation failed",
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="reconcile-tool"
            className="bg-white rounded-[20px] p-5 shadow-[0_6px_24px_rgba(0,0,0,0.07)] mb-5"
        >
            <div className="flex items-center gap-2 mb-2">
                <Search size={20} className="text-forest" />
                <h3 className="font-serif font-black text-navy text-lg">
                    Stripe reconciliation
                </h3>
            </div>
            <p className="text-xs text-muted2 mb-3">
                Paste any Stripe ID — <span className="font-mono">cs_</span>,{" "}
                <span className="font-mono">pi_</span>, or{" "}
                <span className="font-mono">ch_</span>. We pull the truth from
                Stripe and force-sync the order.
            </p>
            <div className="flex gap-2 items-stretch">
                <input
                    type="text"
                    data-testid="reconcile-input"
                    placeholder="cs_live_… or pi_… or ch_…"
                    value={stripeId}
                    onChange={(e) => setStripeId(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && reconcile()}
                    className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-mint text-sm font-mono focus:outline-none focus:border-forest"
                />
                <button
                    type="button"
                    data-testid="reconcile-go"
                    onClick={reconcile}
                    disabled={busy || !stripeId.trim()}
                    className="inline-flex items-center gap-1.5 px-4 rounded-xl bg-forest hover:bg-forestDark text-white font-bold text-sm disabled:opacity-50 active:scale-95 transition-all"
                >
                    {busy ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <Send size={14} />
                    )}
                    Reconcile
                </button>
            </div>

            {result && (
                <div
                    data-testid="reconcile-result"
                    className="mt-4 bg-cream/40 rounded-xl p-3 text-xs space-y-1"
                >
                    <p>
                        <strong>Stripe payment_status:</strong>{" "}
                        <span className="font-mono">
                            {result.stripe_payment_status || "—"}
                        </span>
                    </p>
                    {result.stripe_amount_total != null && (
                        <p>
                            <strong>Amount:</strong> $
                            {(result.stripe_amount_total / 100).toFixed(2)}
                        </p>
                    )}
                    {result.stripe_customer_email && (
                        <p>
                            <strong>Email:</strong>{" "}
                            {result.stripe_customer_email}
                        </p>
                    )}
                    {result.stripe_session_id && (
                        <p className="font-mono break-all">
                            <strong>session:</strong> {result.stripe_session_id}
                        </p>
                    )}
                    {result.stripe_payment_intent_id && (
                        <p className="font-mono break-all">
                            <strong>payment_intent:</strong>{" "}
                            {result.stripe_payment_intent_id}
                        </p>
                    )}
                    <p
                        className={`px-2 py-1 rounded-full inline-block text-[10px] font-bold uppercase tracking-widest ${
                            result.action_taken?.includes("marked_paid")
                                ? "bg-forest text-white"
                                : "bg-mint text-forest"
                        }`}
                    >
                        Action: {result.action_taken}
                    </p>
                    {result.order && (
                        <div className="mt-2 border-t border-mint pt-2">
                            <p className="font-bold text-navy text-sm">
                                Order: {result.order.order_id} · ${result.order.total?.toFixed(2)}
                            </p>
                            <p className="text-muted2">
                                {result.order.customer?.name} · {result.order.customer?.email}
                            </p>
                            <p>
                                <DollarSign
                                    size={11}
                                    className="inline mr-0.5"
                                />
                                payment_status:{" "}
                                <span className="font-mono font-bold text-forest">
                                    {result.order.payment_status}
                                </span>
                                {" · status: "}
                                <span className="font-mono font-bold">
                                    {result.order.status}
                                </span>
                            </p>
                        </div>
                    )}
                    {!result.order && (
                        <p className="text-burnt mt-2">
                            ⚠ No matching order found in our database. This
                            charge happened outside our system or was never
                            saved.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
